import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { Calendar, AlertTriangle, CheckCircle, Clock, TrendingUp, Users, Search, Plus, RefreshCw } from 'lucide-react';

const HRReviewTracker = () => {
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarSync, setCalendarSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('overdue'); // overdue, upcoming, completed, all
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/reviews`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch reviews');

      const data = await response.json();
      setEmployees(data.employees || []);
      setReviews(data.reviews || []);
      setCalendarEvents(data.calendar_events || []);
      setCalendarSync(data.calendar_sync || null);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncFromCalendar = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/reviews/sync-calendar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Sync failed');
      toast.success(`Synced ${data.events} review event${data.events === 1 ? '' : 's'} from the 365 calendar — ${data.matched} matched, ${data.updated} employee${data.updated === 1 ? '' : 's'} updated`);
      if ((data.unmatched || []).length > 0) {
        toast.warning(`Couldn't match ${data.unmatched.length} event(s) to an employee:\n${data.unmatched.join('\n')}`, { duration: 12000 });
      }
      fetchReviews();
    } catch (error) {
      toast.error(error.message || 'Calendar sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const getReviewStatus = (employee) => {
    const now = new Date();
    const lastReview = employee.last_review_date ? new Date(employee.last_review_date) : null;
    const nextReview = employee.next_review_date ? new Date(employee.next_review_date) : null;

    if (!nextReview) {
      return {
        status: 'no_schedule',
        label: 'Not Scheduled',
        color: 'bg-gray-100 text-gray-700',
        priority: 4
      };
    }

    const daysUntil = Math.ceil((nextReview - now) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      return {
        status: 'overdue',
        label: `${Math.abs(daysUntil)} days overdue`,
        color: 'bg-red-100 text-red-700 border-red-300',
        priority: 1
      };
    } else if (daysUntil <= 14) {
      return {
        status: 'urgent',
        label: `${daysUntil} days`,
        color: 'bg-orange-100 text-orange-700 border-orange-300',
        priority: 2
      };
    } else if (daysUntil <= 30) {
      return {
        status: 'upcoming',
        label: `${daysUntil} days`,
        color: 'bg-yellow-100 text-yellow-700 border-yellow-300',
        priority: 3
      };
    } else {
      return {
        status: 'scheduled',
        label: `${daysUntil} days`,
        color: 'bg-green-100 text-green-700 border-green-300',
        priority: 5
      };
    }
  };

  const getFilteredEmployees = () => {
    let filtered = employees;

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(emp => {
        const status = getReviewStatus(emp);
        if (filter === 'overdue') return status.status === 'overdue';
        if (filter === 'upcoming') return status.status === 'urgent' || status.status === 'upcoming';
        if (filter === 'completed') return emp.last_review_date && new Date(emp.last_review_date) > new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
        return true;
      });
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(emp =>
        emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by priority (overdue first)
    return filtered.sort((a, b) => {
      const statusA = getReviewStatus(a);
      const statusB = getReviewStatus(b);
      return statusA.priority - statusB.priority;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const scheduleReview = async (employeeId, date) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/reviews/schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ employee_id: employeeId, review_date: date })
      });

      if (response.ok) {
        fetchReviews();
      }
    } catch (error) {
      console.error('Error scheduling review:', error);
    }
  };

  const completeReview = async (employeeId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/reviews/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ employee_id: employeeId })
      });

      if (response.ok) {
        fetchReviews();
      }
    } catch (error) {
      console.error('Error completing review:', error);
    }
  };

  const getSummaryStats = () => {
    const now = new Date();
    const overdue = employees.filter(emp => {
      const status = getReviewStatus(emp);
      return status.status === 'overdue';
    }).length;

    const urgent = employees.filter(emp => {
      const status = getReviewStatus(emp);
      return status.status === 'urgent';
    }).length;

    const upcoming = employees.filter(emp => {
      const status = getReviewStatus(emp);
      return status.status === 'upcoming';
    }).length;

    const noSchedule = employees.filter(emp => {
      const status = getReviewStatus(emp);
      return status.status === 'no_schedule';
    }).length;

    return { overdue, urgent, upcoming, noSchedule };
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading review tracker...</p>
        </div>
      </div>
    );
  }

  const stats = getSummaryStats();
  const filteredEmployees = getFilteredEmployees();

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Employee Review Tracker</h1>
          <p className="text-blue-800">Stay on top of performance reviews and never miss a deadline</p>
        </div>
        <div className="text-right">
          <Button onClick={syncFromCalendar} disabled={syncing} data-testid="review-sync-calendar-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from 365 Calendar'}
          </Button>
          {calendarSync?.last_sync_at && (
            <p className="text-xs text-blue-800 mt-1">
              Last synced {new Date(calendarSync.last_sync_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              {calendarSync.matched != null ? ` · ${calendarSync.matched}/${calendarSync.events} matched` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Upcoming reviews pulled from the shared 365 calendar */}
      {calendarEvents.filter(ev => ev.review_date >= new Date().toISOString().slice(0, 10)).length > 0 && (
        <Card className="mb-6 bg-emerald-50 border-emerald-200" data-testid="review-calendar-events">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-4 w-4 text-emerald-700" />
              <h4 className="font-semibold text-emerald-900 text-sm">Upcoming Reviews on the 365 Calendar</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {calendarEvents.filter(ev => ev.review_date >= new Date().toISOString().slice(0, 10)).map(ev => (
                <div key={ev.id} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${ev.user_id ? 'bg-white border-emerald-200' : 'bg-amber-50 border-amber-300'}`}>
                  <div className="min-w-0">
                    <span className="font-semibold text-gray-900">{ev.employee_name || ev.raw_title}</span>
                    {!ev.user_id && <span className="ml-2 text-[11px] text-amber-700 font-medium">⚠ no employee match — "{ev.raw_title}"</span>}
                    {ev.user_id && ev.employee_name !== ev.raw_title && (
                      <span className="ml-2 text-xs text-slate-500 truncate">{ev.raw_title}</span>
                    )}
                  </div>
                  <span className="font-mono text-xs text-emerald-800 font-bold flex-shrink-0 ml-3">
                    {new Date(ev.review_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alert for Overdue */}
      {stats.overdue > 0 && (
        <Alert className="mb-6 bg-red-50 border-red-200">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <AlertDescription className="text-red-800 font-semibold">
            ⚠️ You have {stats.overdue} overdue review{stats.overdue > 1 ? 's' : ''}! Please schedule them immediately.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card 
          className={`cursor-pointer transition-all ${filter === 'overdue' ? 'ring-2 ring-red-500' : 'hover:shadow-lg'}`}
          onClick={() => setFilter('overdue')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Overdue</p>
                <p className="text-3xl font-bold text-red-600">{stats.overdue}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'upcoming' ? 'ring-2 ring-orange-500' : 'hover:shadow-lg'}`}
          onClick={() => setFilter('upcoming')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Due Soon (30 days)</p>
                <p className="text-3xl font-bold text-orange-600">{stats.urgent + stats.upcoming}</p>
              </div>
              <Clock className="h-10 w-10 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'completed' ? 'ring-2 ring-green-500' : 'hover:shadow-lg'}`}
          onClick={() => setFilter('completed')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Recent (90 days)</p>
                <p className="text-3xl font-bold text-green-600">
                  {employees.filter(emp => emp.last_review_date && new Date(emp.last_review_date) > new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)).length}
                </p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'all' ? 'ring-2 ring-blue-500' : 'hover:shadow-lg'}`}
          onClick={() => setFilter('all')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Not Scheduled</p>
                <p className="text-3xl font-bold text-gray-600">{stats.noSchedule}</p>
              </div>
              <Calendar className="h-10 w-10 text-gray-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter Bar */}
      <div className="mb-6 flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-500" />
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <Button onClick={() => setFilter('all')} variant={filter === 'all' ? 'default' : 'outline'}>
          All Employees
        </Button>
      </div>

      {/* Employee List */}
      <Card>
        <CardContent className="p-6">
          {filteredEmployees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No employees found matching the filter.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEmployees.map((employee) => {
                const status = getReviewStatus(employee);
                return (
                  <div
                    key={employee.id}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${status.color} hover:shadow-md`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-bold text-gray-900">{employee.full_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{employee.email}</p>
                      <div className="flex gap-6 mt-2 text-sm">
                        <div>
                          <span className="text-gray-500">Last Review:</span>
                          <span className="ml-2 font-semibold text-gray-700">{formatDate(employee.last_review_date)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Next Review:</span>
                          <span className="ml-2 font-semibold text-gray-700">{formatDate(employee.next_review_date)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      {status.status === 'no_schedule' ? (
                        <Button
                          size="sm"
                          onClick={() => {
                            const date = new Date();
                            date.setFullYear(date.getFullYear() + 1);
                            scheduleReview(employee.id, date.toISOString().split('T')[0]);
                          }}
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Schedule
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => completeReview(employee.id)}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Complete
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const date = prompt('Enter next review date (YYYY-MM-DD):');
                          if (date) scheduleReview(employee.id, date);
                        }}
                      >
                        <Calendar className="h-4 w-4 mr-1" />
                        Reschedule
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">Review Best Practices</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Schedule annual reviews 1 year from hire date or last review</li>
                <li>• For probationary employees, schedule 90-day review</li>
                <li>• Set calendar reminders 2 weeks before review date</li>
                <li>• Document all reviews and keep in employee file</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRReviewTracker;
